/**
 * =============================================================================
 * PINNED ALERT CARD COMPONENT
 * =============================================================================
 *
 * Full alert card displayed at top of chat window.
 * Shows: Alert Label, Priority, Timestamps, Title, Context Filters,
 *        Breaching Entities Table, Actions
 *
 * DATA MAPPING:
 * - alert.title → Card header (from "heading" in API)
 * - alert.alertLabel → Classification badge (from "alertLabel" in API)
 * - alert.alertActiveFrom → "Created at" timestamp
 * - alert.dueDate (alertActiveUntil) → "Due by" timestamp
 * - alert.contextFilters → Context pills (from data.filters.a.sourceParams)
 * - alert.breachingEntities → Data table (from data.breachingEntities.a)
 * - alert.rawSupportingData → Used to determine which columns are clickable
 * - alert.actions → Action buttons (from "actions" key, hidden if null)
 *
 * CLICKABLE KEYS LOGIC:
 * A key in breachingEntities is clickable if resolveAllMatchingData (from pattern-resolver.ts)
 * finds matching drill-down data. This handles all response patterns including Pattern B
 * where product_id maps to grouping.PRODUCT.key in supportingData.
 */

import {
    ActionIcon,
    Box,
    Menu,
    Stack,
    Text,
    Badge,
    Button,
    Group,
    Tooltip,
} from '@mantine/core';
import {
    DotsThreeVertical,
    ClockCounterClockwise,
    EyeSlash,
    CheckCircle,
    Clock,
    CalendarBlank,
    Funnel,
    Tag,
    Package,
    Storefront,
    Calendar,
} from '@phosphor-icons/react';
import { SNOOZE_OPTIONS } from './types';
import { memo, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';
import type {
    AlertItem,
    AlertAction,
    Priority,
    AlertContextFilters,
    BreachingEntity,
} from './types';
import { resolveAllMatchingData } from './utils/pattern-resolver';
import { buildAlertCta } from './utils/alert-cta';
import { formatCount } from '@utils/currency';

interface PinnedAlertCardProps {
    alert: AlertItem;
    onActionClick: (action: AlertAction) => void;
    isLoading: boolean;
    disableActions: boolean;
    /** Generic callback for when a clickable entity key is clicked */
    onEntityClick?: (key: string, value: string, entityIndex: number) => void;
    /** Currently selected entity index (for highlighting) */
    selectedEntityIndex?: number;
    /** Whether the alert is in a terminal state (completed/ignored) */
    isAlertDone?: boolean;
    /** Handler for marking complete */
    onComplete?: () => void;
    /** Handler for snoozing — receives minutes */
    onSnooze?: (minutes: number) => void;
    /** Handler for opening ignore modal */
    onShowIgnoreModal?: () => void;
}

// =============================================================================
// CONFIGURATION
// =============================================================================

const priorityConfig: Record<Priority, { color: string; bgColor: string }> = {
    urgent: { color: 'text-halt-800', bgColor: 'bg-halt-400/30' },
    high: { color: 'text-orange-800', bgColor: 'bg-orange-100' },
    medium: { color: 'text-mustard-900', bgColor: 'bg-yellow-50' },
    low: { color: 'text-gray-600', bgColor: 'bg-gray-100' },
};

// Keys to skip when auto-detecting columns. merchantId is an internal
// identifier that must never surface in the UI.
const SKIP_KEYS = [
    'groupings',
    'consumption_per_day',
    'consumption_per_month',
    'months_on_hand',
    'merchantId',
    'merchant_id',
];

// =============================================================================
// HELPER: Format dates for display
// =============================================================================

const formatTimestamp = (dateString?: string): string | null => {
    if (!dateString) return null;
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return null;
    }
};

const formatDateRange = (
    startDate?: string,
    endDate?: string,
): string | null => {
    if (!startDate || !endDate) return null;
    try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const formatOptions: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: 'numeric',
        };
        return `${start.toLocaleDateString('en-IN', formatOptions)} - ${end.toLocaleDateString('en-IN', formatOptions)}`;
    } catch {
        return null;
    }
};

// =============================================================================
// HELPER: Generic table columns detection (first-level primitives only)
// =============================================================================

interface TableColumn {
    key: string;
    label: string;
    format?: 'number' | 'decimal' | 'date' | 'text';
    width?: string;
    truncate?: boolean;
}

// Matches "2026-04-27" and full ISO timestamps like "2026-04-27T18:30:00.000Z"
const ISO_DATE_PATTERN =
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

// Field names whose auto-humanized label reads wrong in supply-chain terms.
// actualQuantity holds GRN-confirmed received units, so the PO tracker calls
// it Received Quantity — keep the breach table consistent with that.
const COLUMN_LABEL_OVERRIDES: Record<string, string> = {
    actualQuantity: 'Received Quantity',
};

/**
 * Detect columns from the first entity (first-level primitive values only).
 * When the alert carries displayColumns (the fields the user agreed to see
 * during creation), show only those, in that order; otherwise show all.
 */
const getTableColumns = (
    entities: BreachingEntity[],
    displayColumns?: string[],
): TableColumn[] => {
    if (entities.length === 0) return [];

    const firstEntity = entities[0];

    const projected = displayColumns?.filter((key) => key in firstEntity);
    const keys =
        projected && projected.length > 0
            ? projected
            : Object.keys(firstEntity);

    const columns: TableColumn[] = [];

    keys.forEach((key) => {
        // Skip keys in the skip list
        if (SKIP_KEYS.includes(key)) return;

        // Skip nested objects (second-level JSON)
        const value = firstEntity[key];
        if (typeof value === 'object' && value !== null) return;

        // Generate human-readable label from key
        const label =
            COLUMN_LABEL_OVERRIDES[key] ??
            key
                .replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .split(' ')
                .map(
                    (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase(),
                )
                .join(' ')
                .trim();

        // Infer format from value type
        const format =
            typeof value === 'number'
                ? 'number'
                : typeof value === 'string' && ISO_DATE_PATTERN.test(value)
                  ? 'date'
                  : 'text';

        columns.push({ key, label, format });
    });

    return columns;
};

/**
 * Determine which keys are clickable in the breaching entities table.
 *
 * PRIMARY path — entity-linked fields (generator-based):
 *   If the supportingDataGenerator has steps referencing $ctx.selectedEntity.<field>,
 *   those fields are entity-linked. Any entity row that has such a field is clickable.
 *   This works even when rawSupportingData is null (all-entity-scope generators).
 *
 * LEGACY path — rawSupportingData matching:
 *   For alerts with alert-scope supporting data, use resolveAllMatchingData to find
 *   which entity keys map to arrays in rawSupportingData. Kept for backward compat.
 */
const getClickableKeys = (
    entities: BreachingEntity[],
    rawSupportingData?: Record<string, unknown>,
    entityLinkedFields?: string[],
): string[] => {
    if (entities.length === 0) return [];
    const firstEntity = entities[0];

    // PRIMARY: entity-linked fields from generator config
    if (entityLinkedFields && entityLinkedFields.length > 0) {
        return entityLinkedFields.filter(
            (field) => field in firstEntity && firstEntity[field] != null,
        );
    }

    // LEGACY: find keys that match arrays in rawSupportingData
    if (!rawSupportingData || Object.keys(rawSupportingData).length === 0)
        return [];
    const clickableKeys: string[] = [];
    for (const key of Object.keys(firstEntity)) {
        const value = firstEntity[key];
        if (typeof value === 'object' && value !== null) continue;
        if (value === null || value === undefined) continue;
        const drillDownConfigs = resolveAllMatchingData(
            rawSupportingData,
            key,
            String(value),
        );
        if (drillDownConfigs.length > 0) clickableKeys.push(key);
    }
    return clickableKeys;
};

const formatValue = (value: unknown, format?: string): string => {
    if (value === null || value === undefined) return '-';
    if (format === 'number')
        return formatCount(Number(value), { maxDecimals: 1 });
    if (format === 'decimal') return Number(value).toFixed(1);
    if (format === 'date') {
        try {
            return new Date(String(value)).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        } catch {
            return String(value);
        }
    }
    return String(value);
};

// =============================================================================
// SUB-COMPONENT: Context Filter Pills
// =============================================================================

interface ContextPillsProps {
    filters?: AlertContextFilters;
}

const EXCLUDED_PILL_KEYS = new Set([
    'merchantId',
    'merchant_id',
    'page',
    'pageSize',
    'page_size',
    'sortBy',
    'sort_by',
    'search',
    'isActive',
    'is_active',
    'productIds',
    'product_ids',
    'productGroups',
    'product_groups',
]);

const PILL_ICON_MAP: Record<string, React.ReactNode> = {
    channels: <Storefront size={12} weight="fill" />,
    vendors: <Package size={12} weight="fill" />,
    type: <Tag size={12} weight="fill" />,
    categories: <Tag size={12} weight="fill" />,
};

const humanizePillKey = (key: string): string =>
    key
        .replace(/_limit$/, ' limit')
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase();

const ContextPills: React.FC<ContextPillsProps> = memo(({ filters }) => {
    if (!filters) return null;

    const pills: { icon: React.ReactNode; label: string; value: string }[] = [];

    const startDate = filters.startDate as string | undefined;
    const endDate = filters.endDate as string | undefined;

    for (const [key, val] of Object.entries(filters)) {
        if (EXCLUDED_PILL_KEYS.has(key)) continue;
        if (val === null || val === undefined) continue;
        if (key === 'startDate' || key === 'endDate') continue;

        let displayValue: string;
        if (Array.isArray(val)) {
            if (val.length === 0) continue;
            displayValue = (val as unknown[]).map(String).join(', ');
        } else if (typeof val === 'string') {
            if (!val) continue;
            displayValue = val;
        } else if (typeof val === 'number') {
            displayValue = `${humanizePillKey(key)}: ${val}`;
        } else {
            continue;
        }

        pills.push({
            icon: PILL_ICON_MAP[key] ?? <Funnel size={12} weight="fill" />,
            label: humanizePillKey(key),
            value: displayValue,
        });
    }

    // Date range pill
    const dateRange = formatDateRange(startDate, endDate);
    if (dateRange) {
        pills.push({
            icon: <Calendar size={12} weight="fill" />,
            label: 'Period',
            value: dateRange,
        });
    }

    if (pills.length === 0) return null;

    return (
        <Box className="rounded-lg border border-slate-100 bg-slate-50/80 p-3">
            <Group className="mb-2 items-center gap-1.5">
                <Funnel size={12} weight="fill" className="text-slate-500" />
                <Text className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                    Context
                </Text>
            </Group>
            <Group className="flex-wrap gap-2">
                {pills.map((pill, idx) => (
                    <Tooltip
                        key={idx}
                        label={`${pill.label}: ${pill.value}`}
                        withArrow
                    >
                        <Badge
                            size="sm"
                            variant="light"
                            color="gray"
                            leftSection={pill.icon}
                            className="border border-slate-200 bg-white font-medium text-slate-700"
                        >
                            {pill.value}
                        </Badge>
                    </Tooltip>
                ))}
            </Group>
        </Box>
    );
});

ContextPills.displayName = 'ContextPills';

// =============================================================================
// SUB-COMPONENT: Breaching Entities Table (Generic)
// =============================================================================

interface BreachingEntitiesTableProps {
    entities: BreachingEntity[];
    rawSupportingData?: Record<string, unknown>;
    entityLinkedFields?: string[];
    displayColumns?: string[];
    onEntityClick?: (key: string, value: string, entityIndex: number) => void;
    selectedEntityIndex?: number;
}

const BreachingEntitiesTable: React.FC<BreachingEntitiesTableProps> = memo(
    ({
        entities,
        rawSupportingData,
        entityLinkedFields,
        displayColumns,
        onEntityClick,
        selectedEntityIndex,
    }) => {
        // Columns the user agreed to during creation, falling back to auto-detection
        const columns = useMemo(
            () => getTableColumns(entities, displayColumns),
            [entities, displayColumns],
        );

        // Determine which keys are clickable
        const clickableKeys = useMemo(
            () =>
                getClickableKeys(
                    entities,
                    rawSupportingData,
                    entityLinkedFields,
                ),
            [entities, rawSupportingData, entityLinkedFields],
        );

        // Find the first clickable key (used for default click behavior)
        const firstClickableKey =
            clickableKeys.length > 0 ? clickableKeys[0] : null;

        // Track if we've already auto-clicked to prevent infinite loops
        const hasAutoClicked = useRef(false);

        // Reset auto-click tracking when entities change
        useEffect(() => {
            hasAutoClicked.current = false;
        }, [entities]);

        // Auto-click first entity on mount if we have clickable keys
        // Use ref to prevent infinite loops from onEntityClick reference changes
        useEffect(() => {
            if (
                firstClickableKey &&
                entities.length > 0 &&
                onEntityClick &&
                selectedEntityIndex === undefined &&
                !hasAutoClicked.current
            ) {
                const firstValue = entities[0][firstClickableKey];
                if (firstValue !== undefined && firstValue !== null) {
                    hasAutoClicked.current = true;
                    onEntityClick(firstClickableKey, String(firstValue), 0);
                }
            }
        }, [firstClickableKey, entities, onEntityClick, selectedEntityIndex]);

        if (!entities || entities.length === 0 || columns.length === 0)
            return null;

        // DataSheet-style tokens (matching the main DataSheet component)
        const tokens = {
            colors: {
                headerBg: '#FAFAFA', // Opaque for sticky header
                headerBgSticky: '#F5F5F5', // Slightly darker for sticky
                surface: '#FFFFFF', // Opaque white for cells
                border: 'rgba(0, 0, 0, 0.06)',
                borderSubtle: 'rgba(0, 0, 0, 0.04)',
                textPrimary: 'rgb(26, 32, 44)',
                textSecondary: 'rgb(100, 116, 139)',
                hoverRow: 'rgba(0, 0, 0, 0.015)',
                selectedRow: 'rgba(1, 149, 255, 0.06)',
                activeLink: 'rgb(3, 105, 161)',
            },
            typography: {
                header: {
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.03em',
                },
                cell: { fontSize: 13 },
            },
            dimensions: {
                rowHeight: 46,
                minColumnWidth: 120,
            },
        };

        return (
            <Box
                style={{
                    backgroundColor: tokens.colors.surface,
                    borderRadius: 12,
                    border: `1px solid ${tokens.colors.border}`,
                    overflow: 'hidden',
                }}
            >
                <Group
                    justify="space-between"
                    style={{
                        padding: '12px 16px 8px',
                        borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                        backgroundColor: tokens.colors.surface,
                    }}
                >
                    <Text
                        style={{
                            ...tokens.typography.header,
                            color: tokens.colors.textSecondary,
                        }}
                    >
                        Alert Data ({entities.length})
                    </Text>
                </Group>

                {/* ScrollArea for both horizontal and vertical scroll */}
                <Box style={{ maxHeight: 280, overflow: 'auto' }}>
                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth:
                                columns.length *
                                tokens.dimensions.minColumnWidth,
                        }}
                    >
                        {/* Sticky Header */}
                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 20,
                                backgroundColor: tokens.colors.headerBg,
                            }}
                        >
                            <tr>
                                {columns.map((col, idx) => (
                                    <th
                                        key={col.key}
                                        style={{
                                            padding: '10px 16px',
                                            textAlign: 'left',
                                            borderBottom: `1px solid ${tokens.colors.border}`,
                                            borderRight:
                                                idx < columns.length - 1
                                                    ? `1px solid ${tokens.colors.borderSubtle}`
                                                    : 'none',
                                            width:
                                                col.width ||
                                                tokens.dimensions
                                                    .minColumnWidth,
                                            minWidth:
                                                tokens.dimensions
                                                    .minColumnWidth,
                                            whiteSpace: 'nowrap',
                                            backgroundColor:
                                                tokens.colors.headerBg,
                                            ...tokens.typography.header,
                                            color: tokens.colors.textSecondary,
                                        }}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {entities.map((entity, idx) => {
                                const isSelected = selectedEntityIndex === idx;
                                // Generate a unique key from the entity
                                const entityKey =
                                    Object.values(entity).find(
                                        (v) => typeof v === 'string' && v,
                                    ) || idx;

                                return (
                                    <tr
                                        key={String(entityKey)}
                                        style={{
                                            backgroundColor: isSelected
                                                ? tokens.colors.selectedRow
                                                : tokens.colors.surface,
                                            transition:
                                                'background-color 0.1s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSelected)
                                                e.currentTarget.style.backgroundColor =
                                                    tokens.colors.hoverRow;
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected)
                                                e.currentTarget.style.backgroundColor =
                                                    isSelected
                                                        ? tokens.colors
                                                              .selectedRow
                                                        : tokens.colors.surface;
                                        }}
                                    >
                                        {columns.map((col, colIdx) => {
                                            const cellValue = entity[col.key];
                                            // Only the FIRST clickable key should be clickable
                                            const isClickable =
                                                col.key === firstClickableKey &&
                                                !!onEntityClick;
                                            const stringValue =
                                                cellValue !== null &&
                                                cellValue !== undefined
                                                    ? String(cellValue)
                                                    : '';

                                            return (
                                                <td
                                                    key={col.key}
                                                    style={{
                                                        padding: '10px 16px',
                                                        borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                                        borderRight:
                                                            colIdx <
                                                            columns.length - 1
                                                                ? `1px solid ${tokens.colors.borderSubtle}`
                                                                : 'none',
                                                        height: tokens
                                                            .dimensions
                                                            .rowHeight,
                                                        verticalAlign: 'middle',
                                                        backgroundColor:
                                                            tokens.colors
                                                                .surface,
                                                        ...tokens.typography
                                                            .cell,
                                                        color: tokens.colors
                                                            .textPrimary,
                                                    }}
                                                >
                                                    {isClickable &&
                                                    stringValue ? (
                                                        <button
                                                            type="button"
                                                            style={{
                                                                background:
                                                                    'none',
                                                                border: 'none',
                                                                padding: 0,
                                                                cursor: 'pointer',
                                                                fontSize: 13,
                                                                fontWeight: 600,
                                                                color: tokens
                                                                    .colors
                                                                    .activeLink,
                                                                textDecoration:
                                                                    isSelected &&
                                                                    col.key ===
                                                                        firstClickableKey
                                                                        ? 'underline'
                                                                        : 'none',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                            onClick={() => {
                                                                if (
                                                                    onEntityClick
                                                                ) {
                                                                    onEntityClick(
                                                                        col.key,
                                                                        stringValue,
                                                                        idx,
                                                                    );
                                                                }
                                                            }}
                                                            onMouseEnter={(
                                                                e,
                                                            ) => {
                                                                e.currentTarget.style.textDecoration =
                                                                    'underline';
                                                            }}
                                                            onMouseLeave={(
                                                                e,
                                                            ) => {
                                                                e.currentTarget.style.textDecoration =
                                                                    isSelected &&
                                                                    col.key ===
                                                                        firstClickableKey
                                                                        ? 'underline'
                                                                        : 'none';
                                                            }}
                                                        >
                                                            {stringValue}
                                                        </button>
                                                    ) : col.truncate ? (
                                                        <Tooltip
                                                            label={String(
                                                                cellValue ||
                                                                    '-',
                                                            )}
                                                            withArrow
                                                            multiline
                                                            maw={300}
                                                        >
                                                            <span
                                                                style={{
                                                                    display:
                                                                        'block',
                                                                    maxWidth:
                                                                        col.width,
                                                                    overflow:
                                                                        'hidden',
                                                                    textOverflow:
                                                                        'ellipsis',
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                {formatValue(
                                                                    cellValue,
                                                                    col.format,
                                                                )}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        <span
                                                            style={{
                                                                fontSize: 13,
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {formatValue(
                                                                cellValue,
                                                                col.format,
                                                            )}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>
            </Box>
        );
    },
);

BreachingEntitiesTable.displayName = 'BreachingEntitiesTable';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const PinnedAlertCard: React.FC<PinnedAlertCardProps> = memo(
    ({
        alert,
        onActionClick,
        isLoading,
        disableActions,
        onEntityClick,
        selectedEntityIndex,
        isAlertDone,
        onComplete,
        onSnooze,
        onShowIgnoreModal,
    }) => {
        const priority =
            priorityConfig[alert.priority] || priorityConfig.medium;

        // Format timestamps
        const createdAt = formatTimestamp(alert.alertActiveFrom);
        const dueBy = formatTimestamp(alert.dueDate);

        // Check if we have actions from the API
        const hasActions = alert.actions && alert.actions.length > 0;

        // Get breaching entities (prefer parsed, fallback to legacy)
        const breachingEntities = alert.breachingEntities || [];

        // System action alerts (channel restock) offer a one-click CTA — the same link the Home
        // page's "Actions to take" section shows (see utils/alert-cta.ts).
        const navigate = useNavigate();
        const planDeepLink = useMemo(() => buildAlertCta(alert), [alert]);

        return (
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <Box
                    className="border-blu-200 relative overflow-hidden rounded-xl border shadow-sm"
                    style={{
                        background:
                            'linear-gradient(135deg, rgba(239,246,255,0.8) 0%, rgba(255,255,255,0.95) 50%, rgba(248,250,252,0.9) 100%)',
                        boxShadow:
                            '0 4px 20px rgba(59,130,246,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
                    }}
                >
                    {/* 3-dot options menu — hidden when alert is done */}
                    {!isAlertDone &&
                        onComplete &&
                        onSnooze &&
                        onShowIgnoreModal && (
                            <Box className="absolute top-2 right-2">
                                <Menu
                                    shadow="md"
                                    width={200}
                                    position="bottom-end"
                                    withArrow
                                >
                                    <Menu.Target>
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            disabled={isLoading}
                                            aria-label="Alert options"
                                        >
                                            <DotsThreeVertical
                                                size={16}
                                                weight="bold"
                                            />
                                        </ActionIcon>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Item
                                            leftSection={
                                                <CheckCircle size={16} />
                                            }
                                            onClick={onComplete}
                                            color="green"
                                        >
                                            Mark as Complete
                                        </Menu.Item>
                                        <Menu.Divider />
                                        <Menu.Label>Snooze Alert</Menu.Label>
                                        {SNOOZE_OPTIONS.map((option) => (
                                            <Menu.Item
                                                key={option.value}
                                                leftSection={
                                                    <ClockCounterClockwise
                                                        size={16}
                                                    />
                                                }
                                                onClick={() =>
                                                    onSnooze(option.value)
                                                }
                                            >
                                                {option.label}
                                            </Menu.Item>
                                        ))}
                                        <Menu.Divider />
                                        <Menu.Item
                                            leftSection={<EyeSlash size={16} />}
                                            onClick={onShowIgnoreModal}
                                        >
                                            Ignore Alert
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            </Box>
                        )}

                    <Stack className="gap-3 p-4">
                        {/* Header Row: Alert Type Badge + Priority Badge */}
                        <Group className="items-start justify-between pr-16">
                            <Group className="flex-wrap gap-2">
                                {/* Alert Type Label - using alertLabel or alertTypeLabel */}
                                <Badge
                                    size="sm"
                                    variant="light"
                                    color="blue"
                                    className="font-semibold"
                                >
                                    {alert.alertLabel || alert.alertTypeLabel}
                                </Badge>
                                {/* Priority Badge */}
                                <Badge
                                    size="sm"
                                    variant="light"
                                    className={`${priority.bgColor} ${priority.color} font-semibold capitalize`}
                                >
                                    {alert.priority}
                                </Badge>
                            </Group>
                        </Group>

                        {/* Timestamps Row: Created at + Due by */}
                        {(createdAt || dueBy) && (
                            <Group className="flex-wrap gap-4">
                                {createdAt && (
                                    <Group className="items-center gap-1.5">
                                        <CalendarBlank
                                            size={12}
                                            weight="fill"
                                            className="text-gray-400"
                                        />
                                        <Text className="text-xs text-gray-500">
                                            Created {createdAt}
                                        </Text>
                                    </Group>
                                )}
                                {dueBy && (
                                    <Group className="items-center gap-1.5">
                                        <Clock
                                            size={12}
                                            weight="fill"
                                            className="text-amber-500"
                                        />
                                        <Text className="text-xs font-medium text-amber-700">
                                            Due {dueBy}
                                        </Text>
                                    </Group>
                                )}
                            </Group>
                        )}

                        {/* Title (from heading) */}
                        <Text className="text-base leading-tight font-semibold text-gray-900">
                            {alert.title}
                        </Text>

                        {/* Context Filter Pills */}
                        <ContextPills filters={alert.contextFilters} />

                        {/* Breaching Entities Table */}
                        <BreachingEntitiesTable
                            entities={breachingEntities}
                            rawSupportingData={alert.rawSupportingData}
                            entityLinkedFields={alert.entityLinkedFields}
                            displayColumns={alert.displayColumns}
                            onEntityClick={onEntityClick}
                            selectedEntityIndex={selectedEntityIndex}
                        />

                        {/* Channel-understock alert: open the distribution plan builder for these SKUs */}
                        {planDeepLink && (
                            <Group className="gap-2 pt-1">
                                <Button
                                    size="xs"
                                    color="blue"
                                    onClick={() => navigate(planDeepLink.href)}
                                    className="font-medium"
                                >
                                    {planDeepLink.label}
                                </Button>
                            </Group>
                        )}

                        {/* Actions - only show if actions exist in API response */}
                        {hasActions && (
                            <Stack className="gap-2 pt-1">
                                <Text className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                    Actions
                                </Text>
                                <Group className="flex-wrap gap-2">
                                    {alert.actions!.map((action) => (
                                        <Button
                                            key={action.id}
                                            size="xs"
                                            variant="light"
                                            color="blue"
                                            onClick={() =>
                                                onActionClick(action)
                                            }
                                            loading={isLoading}
                                            disabled={disableActions}
                                            className="font-medium"
                                        >
                                            {action.label}
                                        </Button>
                                    ))}
                                </Group>
                            </Stack>
                        )}
                    </Stack>
                </Box>
            </motion.div>
        );
    },
);

PinnedAlertCard.displayName = 'PinnedAlertCard';

export default PinnedAlertCard;
